import React, { FC, useRef, useEffect } from "react";
import classNames from "classnames";
import loadable from "@loadable/component";
import { Fade } from "react-awesome-reveal";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import { BlockHeader } from "@app/components/common/block-header/BlockHeader";
import { BlockBody } from "@app/components/common/block-body/BlockBody";
import { FileType, checkFileType } from "@app/helpers/check-file-type";
import { IRichText, TextType, IAsset } from "@app/models/storyblok/basic";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { ContentAlign, ViewVariant } from "../../enums";
import { PARALLAX_OPTIONS } from "./constants";

const ReactPlayer = loadable(() => import("react-player"));

type Props = {
  title: IRichText<TextType>;
  media: IAsset;
  text?: IRichText<TextType>;
  overlayColorOpacity?: number;
  contentAlign?: ContentAlign;
  viewVariant?: ViewVariant;
};

export const ImageTitle: FC<Props> = ({
  title,
  media,
  text,
  overlayColorOpacity,
  contentAlign,
  viewVariant,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !mediaRef.current) return;

    const handleScroll = () => {
      if (!containerRef.current || !mediaRef.current) return;

      const parentRect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const parentCenter = parentRect.top + parentRect.height / 2;
      const windowCenter = windowHeight / 2;

      const distanceFromCenter = parentCenter - windowCenter;
      const offset =
        viewVariant === ViewVariant.CONTAINER
          ? Math.min(
              Math.max(
                distanceFromCenter *
                  PARALLAX_OPTIONS.containerOffsetCoefficient *
                  -1,
                parentRect.height * PARALLAX_OPTIONS.offsetCoefficient * -1,
              ),
              parentRect.height * PARALLAX_OPTIONS.offsetCoefficient,
            )
          : distanceFromCenter * PARALLAX_OPTIONS.offsetCoefficient * -1;

      mediaRef.current.style.transform = `translateY(${offset}px)`;
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
      <div
        className={classNames({
          "flex flex-col items-center px-5 lg:px-16 xl:px-24":
            viewVariant === ViewVariant.CONTAINER,
        })}
      >
        <div
          ref={containerRef}
          className={classNames(
            "relative mt-20 w-full overflow-hidden",
            "lg:mt-25",
            "xl:mt-40",
            {
              "h-[100vh]": viewVariant !== ViewVariant.CONTAINER,
              "h-[514px] rounded-lg border border-divider xxl:h-[843px] xxl:max-w-container xxl:rounded-4xl":
                viewVariant === ViewVariant.CONTAINER,
            },
          )}
        >
          <div
            ref={mediaRef}
            className={classNames(
              "absolute left-0 right-0",
              "will-change-transform",
              {
                "bottom-0 top-0": viewVariant !== ViewVariant.CONTAINER,
                "-bottom-[6vh] -top-[6vh] xxl:-bottom-[10vh] xxl:-top-[10vh]":
                  viewVariant === ViewVariant.CONTAINER,
              },
            )}
          >
            {checkFileType(media.filename) === FileType.VIDEO ? (
              <ReactPlayer
                className={
                  "h-full [&>video]:rounded-none [&>video]:object-cover"
                }
                url={media.filename}
                width="100%"
                height="none"
                playing
                muted
                loop
                playsinline
                config={{
                  file: { attributes: { disablePictureInPicture: true } },
                }}
              />
            ) : checkFileType(media.filename) === FileType.IMAGE ? (
              <GatsbyImage
                image={getGatsbyImage(media.filename, { layout: "fullWidth" })}
                alt={media.alt}
                className={"h-full w-full"}
              />
            ) : null}
            <div
              className={
                "absolute bottom-0 left-0 right-0 top-0 bg-base-content"
              }
              style={{ opacity: `${overlayColorOpacity ?? 0}%` }}
            />
          </div>

          <div
            className={classNames(
              "relative flex h-full flex-col justify-center text-primary-content",
              "xxl:mx-auto",
              {
                "items-center": contentAlign === ContentAlign.CENTER,
                "items-start":
                  !contentAlign || contentAlign === ContentAlign.LEFT,
                "items-end": contentAlign === ContentAlign.RIGHT,
                "px-5 lg:px-16 xl:px-24 xxl:max-w-container-wide":
                  viewVariant !== ViewVariant.CONTAINER,
                "p-5 lg:p-8 xxl:max-w-container xxl:p-11":
                  viewVariant === ViewVariant.CONTAINER,
              },
            )}
          >
            <div
              className={classNames(
                "space-y-5 break-words text-large",
                "md:max-w-[70%]",
                "lg:text-xx-large",
                "xxl:max-w-[50%] xxl:text-huge",
                {
                  "text-center": contentAlign === ContentAlign.CENTER,
                  "text-start":
                    !contentAlign || contentAlign === ContentAlign.LEFT,
                  "text-end": contentAlign === ContentAlign.RIGHT,
                },
              )}
            >
              <BlockHeader
                richText={title}
                className={classNames(
                  "text-large",
                  "lg:text-xx-large",
                  "xxl:text-huge",
                )}
              />
              {text && (
                <div
                  className={classNames("text-medium", "lg:text-medium-large")}
                >
                  <BlockBody richText={text} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fade>
  );
};
